// A dropdown that cannot be clipped.
//
// The previous one was a child of the board card, and the card had
// `overflow: hidden` for its rounded preview, so the menu was cut off at the
// card's edge — half a menu, unreachable items. Any fix that leaves the menu
// inside the card is one `overflow` away from breaking again, so it renders
// into a portal on the body and positions itself against the trigger's
// rectangle. Nothing between it and the body can clip it.
//
// It also does the things a menu is supposed to do and the old one did not:
// closes on an outside click, closes on Escape and returns focus to the button,
// and moves through its items with the arrow keys.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dots } from "./icons.jsx";

const GAP = 6;
const MARGIN = 8; // keep this far from the viewport edge

const place = (trigger, menu) => {
  const t = trigger.getBoundingClientRect();
  const m = menu.getBoundingClientRect();

  // Below the trigger unless that would run off the bottom, in which case flip.
  const below = t.bottom + GAP;
  const flip = below + m.height > window.innerHeight - MARGIN && t.top - GAP - m.height > MARGIN;
  const top = flip ? t.top - GAP - m.height : below;

  // Right-aligned with the trigger, pulled back in if that overflows.
  const right = Math.min(
    Math.max(window.innerWidth - t.right, MARGIN),
    window.innerWidth - m.width - MARGIN,
  );

  return { top, right: Math.max(right, MARGIN) };
};

/**
 * @param {{label: string, items: Array<{label, onSelect, danger?, disabled?}|"separator">}} props
 */
export const Menu = ({ label, items, children, className = "btn ghost icon small" }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(
    ({ restoreFocus = false } = {}) => {
      setOpen(false);
      setPos(null);
      if (restoreFocus) triggerRef.current?.focus();
    },
    [],
  );

  // Position after the menu exists but before the browser paints, so it never
  // appears in the wrong place first.
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !triggerRef.current) return;
    setPos(place(triggerRef.current, menuRef.current));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close({ restoreFocus: true });
      }
    };
    // A menu anchored to a rectangle is wrong the moment that rectangle moves.
    const onReflow = () => close();

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, close]);

  const onMenuKeyDown = (event) => {
    const buttons = [...(menuRef.current?.querySelectorAll("button:not(:disabled)") ?? [])];
    const at = buttons.indexOf(document.activeElement);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      buttons[(at + step + buttons.length) % buttons.length]?.focus();
    }
  };

  const usable = items.filter((item) => item !== "separator" && item.onSelect);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={usable.length === 0}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {children ?? <Dots />}
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="menu"
              role="menu"
              onKeyDown={onMenuKeyDown}
              style={{
                top: pos?.top ?? -9999,
                right: pos?.right ?? 0,
                // Hidden for the one frame before it has been measured.
                visibility: pos ? "visible" : "hidden",
              }}
            >
              {items.map((item, i) =>
                item === "separator" ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className="menu-sep" key={`sep-${i}`} />
                ) : item.label && !item.onSelect ? (
                  // A heading rather than a choice — "signed in as", and such.
                  <div className="menu-label" key={item.label}>
                    {item.label}
                  </div>
                ) : (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    className={item.danger ? "danger" : undefined}
                    disabled={item.disabled}
                    onClick={() => {
                      close();
                      item.onSelect();
                    }}
                  >
                    {item.label}
                  </button>
                ),
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};
